import { useEffect, useState } from 'react'
import { Spinner } from '@/components/ds2'
import { safeHref } from '@/components/agent-markdown/renderAgentInline'
import { normalizePreviewBlob } from '@/pages/dashboard/pages/shared-components/pdfPreviewUtils'

/** What a stored file shows as: a PDF or an image drawn here, or neither (offered as a link). */
type PreviewKind = 'pdf' | 'image' | 'other'

const kindOf = (mimeType: string): PreviewKind => (mimeType === 'application/pdf' ? 'pdf' : mimeType.startsWith('image/') ? 'image' : 'other')

/** A stored file read for the drawer: the object URL it is drawn from and its kind, or the read's failure. */
type InlineFile = { state: 'loading' } | { state: 'ready'; url: string; kind: PreviewKind } | { state: 'failed' }

/**
 * Reads a stored file into an object URL typed by what it is. A storage link served as a download (`Content-Disposition: attachment`,
 * as GCS's download links are) renders nothing in an iframe; the same bytes as a typed blob render inline. `expectedType` types a file
 * storage sent untyped. The URL is revoked when the file changes or the view closes.
 */
export const useInlineFile = (url: string | null, title: string, expectedType?: string): InlineFile => {
    const [file, setFile] = useState<InlineFile>({ state: 'loading' })
    useEffect(() => {
        if (!url) {
            setFile({ state: 'failed' })
            return
        }
        let objectUrl: string | null = null
        let live = true
        const abort = new AbortController()
        setFile({ state: 'loading' })
        Promise.resolve()
            .then(() => fetch(url, { signal: abort.signal }))
            .then((response) => {
                if (!response.ok) throw new Error(`The file answered ${response.status}`)
                return response.blob()
            })
            .then((blob) => {
                if (!live) return
                const named = normalizePreviewBlob(blob, url, title)
                // A file the caller knows the type of (an invoice is a PDF) is typed so when storage sent no usable type.
                const typed = expectedType && kindOf(named.type) === 'other' ? new Blob([named], { type: expectedType }) : named
                objectUrl = URL.createObjectURL(typed)
                setFile({ state: 'ready', url: objectUrl, kind: kindOf(typed.type) })
            })
            .catch(() => {
                if (live) setFile({ state: 'failed' })
            })
        return () => {
            live = false
            abort.abort()
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [url, title, expectedType])
    return file
}

interface InlineDocumentProps {
    /** The stored link; read through `useInlineFile` unless the caller already holds the read. */
    url: string
    title: string
    file?: InlineFile
}

/** A stored document drawn in the drawer: the PDF or the image itself, or a link to it when it cannot be drawn here. */
export const InlineDocument = ({ url, title, file: given }: InlineDocumentProps) => {
    const read = useInlineFile(given ? null : url, title)
    const file = given ?? read
    if (file.state === 'loading') {
        return (
            <div className="flex h-40 items-center justify-center gap-2 rounded-[10px] bg-surface-secondary text-sm text-foreground-muted" role="status">
                <Spinner size={16} /> Loading the document…
            </div>
        )
    }
    if (file.state === 'ready' && file.kind === 'pdf') {
        // The drawer is narrow: hide the viewer's page-thumbnail pane and fit the page to the width.
        return (
            <iframe
                title={title}
                src={`${file.url}#navpanes=0&view=FitH`}
                className="h-[70vh] w-full rounded-[10px] border border-border bg-surface-secondary"
            />
        )
    }
    if (file.state === 'ready' && file.kind === 'image') {
        return <img src={file.url} alt={title} className="w-full rounded-[10px] border border-border object-contain" />
    }
    const href = safeHref(url)
    return (
        <p className="text-sm text-foreground-muted">
            This document cannot be shown here.{' '}
            {href && (
                <a href={href} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                    Open it in a new tab
                </a>
            )}
        </p>
    )
}
